<template>
  <cst-page-template>
    <div class="container">
      <div class="bulk-change-order-page--wrapper">
        <loader v-if="isLoading" />
        <div v-if="!isLoading">
          <back-to-dashboard-link :type="dashboardType.changeOrder" />
          <div class="bulk-change-order-content">
            <div class="header">
              <div class="header-item">
                <span>
                  {{ i18n.t('/change-order/bulk/cars-amount') }}
                </span>
                <div class="value">
                  {{ bulkChangeOrder.OriginalExcelRows?.length }}
                </div>
              </div>
              <div class="header-item status">
                <span>
                  {{ i18n.t('/change-order/bulk/file/status') }}
                  {{ getBulkChangeOrderStatus }}
                </span>
                <div class="status-indicator">
                  <div class="progress">
                    <span
                      >{{ getProcessedItems }}/{{ bulkChangeOrder.OriginalExcelRows?.length }}</span
                    >
                  </div>
                  <div class="result success">
                    <span>{{ getItemsProcessedSuccessfully }}</span>
                    <span>
                      {{ i18n.t('/change-order/bulk/success') }}
                    </span>
                  </div>
                  <div class="result failure">
                    <span>{{ getItemsNotProcessedDueToError }}</span>
                    <span>
                      {{ i18n.t('/change-order/bulk/error') }}
                    </span>
                  </div>
                  <div class="result unchanged">
                    <span>{{ getItemsNotProcessedDueToNoChangeNeeded }}</span>
                    <span>
                      {{ i18n.t('/change-order/bulk/no-changes') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="desired-changes">
              <span>
                <strong>
                  {{ i18n.t('/change-order/bulk/changes') }}
                </strong>
              </span>
              <ul>
                <li v-for="summaryItem in getSummaryItems" :key="summaryItem">
                  {{ summaryItem }}
                </li>
              </ul>
            </div>
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      v-for="header in uniqueHeaders"
                      :key="header"
                      :class="determineTableHeaderClass(header)"
                    >
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in bulkChangeOrder.OriginalExcelRows" :key="index">
                    <td
                      v-for="header in uniqueHeaders"
                      :key="header"
                      :class="determineTableCellClass(header)"
                    >
                      <template v-if="header === 'Resultaat'">
                        <span @click="handleResultaatClick(row.OriginalExcelCells)">
                          <i :class="getIconClass(row.OriginalExcelCells)"></i>
                        </span>
                      </template>
                      <template v-else>
                        {{ findValueByHeader(row.OriginalExcelCells, header) }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>

              <change-order-modification-result
                v-if="showOrderModificationResult"
                :order-result="orderResult"
                @close="showOrderModificationResult = false"
              />
            </div>
            <div class="actions">
              <button @click="downloadExcelExport">
                {{ i18n.t('/change-order/bulk/download') }}
              </button>
              <button :disabled="isCheckBulkChangeOrderDisabled" @click="checkBulkChangeOrder">
                {{ i18n.t('/change-order/bulk/check') }}
              </button>
              <button v-if="isBulkChangeOrderPaused" @click="resumeProcessBulkChangeOrder">
                {{ i18n.t('/change-order/bulk/start') }}
              </button>
              <button v-else-if="isBulkChangeOrderStarted" @click="pauseProcessBulkChangeOrder">
                {{ i18n.t('/change-order/bulk/pause') }}
              </button>
              <button
                v-else
                :disabled="isStartProcessBulkChangeOrderDisabled"
                @click="startProcessBulkChangeOrder"
              >
                {{ i18n.t('/change-order/bulk/process') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import BulkChangeOrderService from '@/services/bulkChangeOrderService'
import BackToDashboardLink from '@/components/generic/presentational/molecules/backToDashboardLink/BackToDashboardLink.vue'
import dashboardType from '@/helpers/enums/dashboardType'
import { mapGetters } from 'vuex'
import ChangeOrderModificationResult from '@/components/changeOrder/presentational/organisms/changeOrderActions/ChangeOrderModificationResult.vue'
import BulkChangeOrderStatus from '@/helpers/enums/bulkChangeOrderStatus'

export default {
  components: {
    ChangeOrderModificationResult,
    BackToDashboardLink,
    Loader,
    CstPageTemplate,
  },
  props: {
    bulkChangeOrderId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      dashboardType: dashboardType,
      isLoading: false,
      processBulkChangeOrderStarted: false,
      processBulkChangeOrderPaused: false,
      checkBulkChangeOrderDisabled: false,
      summaryColumns: [
        { header: 'Kleur' },
        { header: 'Bekleding' },
        { header: 'Optie(s) toevoegen' },
        { header: 'Optie(s) verwijderen' },
        { header: 'Sturingscode toevoegen' },
        { header: 'Sturingscode verwijderen' },
        { header: 'Afleverdata' },
        { header: 'Owning dealer wijzigen' },
        { header: 'Loslocatie' },
        { header: 'EigenaarID' },
        { header: 'Reserveren' },
        { header: 'Blokkeren' },
        { header: 'BlokkerenShowroom' },
        { header: 'Commercial hold' },
        { header: 'Uitgesteld transport' },
        { header: 'Kenteken aanvragen' },
        { header: 'Kentekenplaten geel' },
        { header: 'Aantal witte kentekenplaten' },
        { header: 'Taxfree' },
        { header: 'Order annuleren' },
        { header: 'Opmerking' },
      ],
      showOrderModificationResult: false,
      orderResult: null,
    }
  },
  computed: {
    ...mapGetters({
      bulkChangeOrder: 'bulkChangeOrder/getBulkChangeOrder',
    }),
    uniqueHeaders() {
      let originalExcelRows = this.bulkChangeOrder.OriginalExcelRows
      if (originalExcelRows) {
        //Use a map, because not every row has the same amount of headers;
        //We can only sort when we have all the headers and their sortOrder
        const headersWithSortOrder = new Map()

        originalExcelRows.forEach((row) => {
          row.OriginalExcelCells.forEach((cell) => {
            if (!headersWithSortOrder.has(cell.Header)) {
              headersWithSortOrder.set(cell.Header, cell.SortOrder)
            }
          })
        })

        // Convert Map entries to an array and sort them by SortOrder
        return Array.from(headersWithSortOrder.keys()).sort((a, b) => {
          return headersWithSortOrder.get(a) - headersWithSortOrder.get(b)
        })
      }
      return []
    },
    getBulkChangeOrderStatus() {
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.concept) {
        return 'CONCEPT'
      }
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.readyForProcessing) {
        return 'IN WACHTRIJ'
      }
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processing) {
        return 'WORDT VERWERKT'
      }
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.paused) {
        return 'GEPAUZEERD'
      }
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.checked) {
        return 'ORDER GECHECKED'
      }
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processed) {
        return 'ORDER GEWIJZIGD'
      }
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.error) {
        return 'ERROR'
      }
      return 'STATUS ONBEKEND'
    },
    hasCarConfigurationChanges() {
      const headersToCheck = ['Kleur', 'Bekleding', 'Optie(s) toevoegen', 'Optie(s) verwijderen']

      let originalExcelRows = this.bulkChangeOrder.OriginalExcelRows
      for (let row of originalExcelRows) {
        for (let header of headersToCheck) {
          if (row.OriginalExcelCells.find((cell) => cell.Header === 'Kleur')) {
            return true
          }
        }
      }
      return false
    },
    isCheckBulkChangeOrderDisabled() {
      if (
        this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processed ||
        this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processing ||
        this.checkBulkChangeOrderDisabled
      ) {
        return true
      }
      return !this.hasCarConfigurationChanges
    },
    isStartProcessBulkChangeOrderDisabled() {
      return (
        this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processed ||
        this.isBulkChangeOrderStarted
      )
    },
    isBulkChangeOrderStarted() {
      return (
        this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processing ||
        this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.readyForProcessing ||
        this.processBulkChangeOrderStarted
      )
    },
    isBulkChangeOrderPaused() {
      return (
        this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.paused ||
        this.processBulkChangeOrderPaused
      )
    },
    getSummaryItems() {
      return this.summaryColumns
        .map((column) => {
          const summary = this.getSummary(column.header)
          if (summary) {
            return `${column.header} - ${summary}`
          }
          return null
        })
        .filter((item) => item !== null)
    },
    getProcessedItems() {
      if (
        this.bulkChangeOrder.BulkOrderStatus !== BulkChangeOrderStatus.processed ||
        this.bulkChangeOrder.BulkOrderStatus !== BulkChangeOrderStatus.paused
      ) {
        return this.bulkChangeOrder.OriginalExcelRows.filter((row) =>
          row.OriginalExcelCells.some((cell) => cell.Header === 'Resultaat')
        ).length
      }
      return this.bulkChangeOrder.OriginalExcelRows?.length
    },
    getItemsProcessedSuccessfully() {
      return this.bulkChangeOrder.OriginalExcelRows.filter((row) => {
        const changeSuccess = row.OriginalExcelCells.find(
          (cell) => cell.Header === 'Wijziging succesvol'
        )
        const changeHasError = row.OriginalExcelCells.find(
          (cell) => cell.Header === 'Niet gewijzigd'
        )
        const changeNotNeeded = row.OriginalExcelCells.find(
          (cell) => cell.Header === 'Wijziging onnodig'
        )

        return (
          !changeNotNeeded?.Value.trim() &&
          !changeHasError?.Value.trim() &&
          changeSuccess?.Value.trim()
        )
      }).length
    },
    getItemsNotProcessedDueToError() {
      return this.bulkChangeOrder.OriginalExcelRows.filter((row) => {
        const changeHasError = row.OriginalExcelCells.find(
          (cell) => cell.Header === 'Niet gewijzigd'
        )
        return changeHasError?.Value.trim()
      }).length
    },
    getItemsNotProcessedDueToNoChangeNeeded() {
      return this.bulkChangeOrder.OriginalExcelRows.filter((row) => {
        const changeNotNeeded = row.OriginalExcelCells.find(
          (cell) => cell.Header === 'Wijziging onnodig'
        )
        return changeNotNeeded?.Value.trim()
      }).length
    },
  },
  async mounted() {
    this.isLoading = true
    if (this.bulkChangeOrderId) {
      await BulkChangeOrderService.getBulkChangeOrderByBulkChangeOrderId(this.bulkChangeOrderId)
      this.refreshInterval = setInterval(() => {
        this.silentFetchBulkChangeOrder()
      }, 15000)
    }
    this.isLoading = false
  },
  beforeUnmount() {
    this.stopRefreshingInterval()
  },
  methods: {
    async silentFetchBulkChangeOrder() {
      if (this.bulkChangeOrder.BulkOrderStatus === BulkChangeOrderStatus.processed) {
        this.stopRefreshingInterval()
      } else {
        await BulkChangeOrderService.getBulkChangeOrderByBulkChangeOrderId(this.bulkChangeOrderId)
      }
    },
    stopRefreshingInterval() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }
    },
    getIconClass(cells) {
      const cellChangeNotNeeded = cells.find((cell) => cell.Header === 'Wijziging onnodig')
      const cellChangeError = cells.find((cell) => cell.Header === 'Niet gewijzigd')
      const cellChangeSuccess = cells.find((cell) => cell.Header === 'Niet gewijzigd')

      if (cellChangeError?.Value) {
        return 'fas fa-times-circle text-error clickable'
      }
      if (cellChangeNotNeeded?.Value) {
        return 'fas fa-exclamation-triangle text-warning clickable'
      }
      if (cellChangeSuccess) {
        return 'fas fa-check-circle text-success clickable'
      }
      return ''
    },
    findValueByHeader(cells, header) {
      const cell = cells.find((cell) => cell.Header === header)
      return cell ? cell.Value : ''
    },
    handleResultaatClick(cells) {
      const cell = cells.find((cell) => cell.Header === 'Resultaat')
      this.orderResult = cell.Value
      this.showOrderModificationResult = true
    },
    determineTableHeaderClass(header) {
      if (header === 'Wijziging succesvol') {
        return 'changeSuccessHeader'
      }
      if (header === 'Niet gewijzigd') {
        return 'changeErrorHeader'
      }
      if (header === 'Wijziging onnodig') {
        return 'changeNotNeededHeader'
      }
    },
    determineTableCellClass(header) {
      if (header === 'Resultaat') {
        return 'result-icon'
      }
    },
    getSummary(header) {
      let excelValues = this.bulkChangeOrder.OriginalExcelRows
      if (excelValues?.length) {
        let komNumbers = excelValues.filter((row) =>
          row.OriginalExcelCells.some((cell) => cell.Header === header && cell.Value)
        ).length
        if (komNumbers > 0) {
          return komNumbers + ' komnummers'
        }
      }
      return undefined
    },
    async downloadExcelExport() {
      try {
        this.isLoading = true
        await BulkChangeOrderService.downloadExcelExport(this.bulkChangeOrder?.BulkChangeOrderId)
      } catch (e) {
        return Promise.reject(e)
      } finally {
        this.isLoading = false
      }
    },
    async checkBulkChangeOrder() {
      this.isLoading = true

      try {
        await BulkChangeOrderService.checkBulkChangeOrder(this.bulkChangeOrder?.BulkChangeOrderId)
        this.checkBulkChangeOrderDisabled = true
      } catch (error) {
        console.error('Error in checkOrder ', error)
      }
      this.isLoading = false
    },
    async startProcessBulkChangeOrder() {
      this.isLoading = true

      try {
        await BulkChangeOrderService.processBulkChangeOrder(this.bulkChangeOrder?.BulkChangeOrderId)
        this.setProcessBulkChangeOrderState(true)
      } catch (error) {
        console.error('Error in processChanges ', error)
      }
      this.isLoading = false
    },
    async pauseProcessBulkChangeOrder() {
      this.isLoading = true

      try {
        await BulkChangeOrderService.pauseBulkChangeOrder(this.bulkChangeOrder?.BulkChangeOrderId)
        this.setProcessBulkChangeOrderState(false)
        await this.silentFetchBulkChangeOrder()
      } catch (error) {
        console.error('Error in processChanges ', error)
      }
      this.isLoading = false
    },
    async resumeProcessBulkChangeOrder() {
      this.isLoading = true

      try {
        await BulkChangeOrderService.resumeBulkChangeOrder(this.bulkChangeOrder?.BulkChangeOrderId)
        this.setProcessBulkChangeOrderState(true)
        this.bulkChangeOrder.BulkOrderStatus = BulkChangeOrderStatus.readyForProcessing
      } catch (error) {
        console.error('Error in processChanges ', error)
      }
      this.isLoading = false
    },
    setProcessBulkChangeOrderState(isStart) {
      this.processBulkChangeOrderPaused = !isStart
      this.processBulkChangeOrderStarted = isStart
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  padding: 0 1.5rem;
}

.bulk-change-order-page--wrapper {
  padding: 20px;
}

.bulk-change-order-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 50px;
}

.header {
  display: flex;
  justify-content: space-between;
}

.header-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px black solid;
  height: 150px;
  width: 350px;
}

.header-item .value {
  font-size: 2rem;
  font-weight: bold;
}

.status {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.status-indicator {
  display: flex;
  gap: 10px;
}

.progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #ccc;
  padding: 10px;
}

.result {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
}

.success {
  background-color: green;
  color: white;
  text-align: center;
}

.failure {
  background-color: red;
  color: white;
  text-align: center;
}

.unchanged {
  background-color: orange;
  color: white;
  text-align: center;
}

.desired-changes {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 30px;
  margin-bottom: 20px;
}

.table-container {
  max-height: 800px;
  overflow-y: auto;
  overflow-x: auto;
}

table {
  table-layout: auto;
}

thead {
  position: sticky;
  top: 0;
  background-color: #f4f4f4;
  z-index: 1;
}

thead th {
  padding: 8px;
  border: 1px solid #ddd;
}

tbody td {
  padding: 8px;
  border: 1px solid #ddd;
  box-sizing: border-box;
}

.changeSuccessHeader {
  background: rgba(0, 128, 0, 0.4);
}

.changeErrorHeader {
  background: rgba(255, 0, 0, 0.4);
}

.changeNotNeededHeader {
  background: rgba(255, 165, 0, 0.4);
}

.result-icon {
  text-align: center;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  position: sticky;
  bottom: 0;
  background: white;
  padding: 12px 0;
  border-top: 1px solid #ddd;
  z-index: 2;
}

button {
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #0056b3;
}

.clickable:hover {
  cursor: pointer;
  opacity: 0.4;
}

.text-error {
  color: red;
}

.text-warning {
  color: orange;
}

.text-success {
  color: green;
}

button[disabled] {
  background-color: #a3b7c0;
}
</style>
